const BYTES_PER_MEBIBYTE = 1024 * 1024;
const JPEG_QUALITY = 0.9;

const JPEG_EXTENSIONS = ['jpg', 'jpeg'];
const COMPRESSIBLE_IMAGE_EXTENSIONS = [...JPEG_EXTENSIONS, 'png'];
const JPEG_MIME_TYPES = ['image/jpeg', 'image/pjpeg'];
const PNG_MIME_TYPES = ['image/png'];

export type AttachmentImageResizeErrorCode =
  | 'decode-failed'
  | 'resize-failed'
  | 'output-too-large';

export class AttachmentImageResizeError extends Error {
  code: AttachmentImageResizeErrorCode;

  constructor(code: AttachmentImageResizeErrorCode) {
    super(code);
    this.name = 'AttachmentImageResizeError';
    this.code = code;
  }
}

function getExtension(file: File): string | undefined {
  return file.name.split('.').pop()?.toLowerCase();
}

export function isCompressibleAttachmentImage(file: File): boolean {
  if (
    JPEG_MIME_TYPES.includes(file.type) ||
    PNG_MIME_TYPES.includes(file.type)
  ) {
    return true;
  }

  const extension = getExtension(file);
  return extension ? COMPRESSIBLE_IMAGE_EXTENSIONS.includes(extension) : false;
}

function getOutputType(file: File): 'image/jpeg' | 'image/png' {
  const extension = getExtension(file) || '';
  if (
    JPEG_EXTENSIONS.includes(extension) ||
    JPEG_MIME_TYPES.includes(file.type)
  ) {
    return 'image/jpeg';
  }

  return 'image/png';
}

function isDecodeFailure(error: unknown): boolean {
  if (error instanceof Event) {
    return true;
  }

  return (
    error instanceof DOMException &&
    ['DataError', 'EncodingError', 'InvalidStateError'].includes(error.name)
  );
}

export async function resizeAttachmentImage(
  file: File,
  maximumFileSize: number,
): Promise<File> {
  const outputType = getOutputType(file);
  const compressionInput =
    file.type === outputType
      ? file
      : new File([file], file.name, {
          type: outputType,
          lastModified: file.lastModified,
        });

  let compressedFile: File;
  try {
    const { default: imageCompression } = await import(
      'browser-image-compression'
    );
    compressedFile = await imageCompression(compressionInput, {
      maxSizeMB: maximumFileSize / BYTES_PER_MEBIBYTE,
      useWebWorker: false,
      preserveExif: false,
      fileType: outputType,
      initialQuality: outputType === 'image/jpeg' ? JPEG_QUALITY : undefined,
    });
  } catch (error) {
    throw new AttachmentImageResizeError(
      isDecodeFailure(error) ? 'decode-failed' : 'resize-failed',
    );
  }

  if (compressedFile.size > maximumFileSize) {
    throw new AttachmentImageResizeError('output-too-large');
  }

  return new File([compressedFile], file.name, {
    type: outputType,
    lastModified: file.lastModified,
  });
}
